import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Zap, Calculator, Settings, FileSpreadsheet, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

// ⚠️ Content review needed before this page goes live: the exact mechanics of
// tube laser costing (supported profile shapes, how dimensions/length are
// entered, nesting/optimization behavior, any size limits) could not be
// verified against the app's source from this repo. Every {/* TODO */}
// marker below needs a real answer — copy currently stays deliberately
// generic/outcome-level rather than guessing at specifics.
const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Κοστολόγηση Κοπής Laser Σωλήνα',
      subtitle: 'Υπολογίστε το κόστος κοπής laser για μεταλλικούς σωλήνες και προφίλ αυτόματα, με τον ίδιο μηχανισμό κοστολόγησης που χρησιμοποιείτε ήδη για έλασμα.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Επικοινωνήστε μαζί μας',
    },
    intro: {
      title: 'Κοστολόγηση σωλήνα, όχι μόνο ελάσματος',
      // TODO: replace with the real description of how tube laser costing
      // works once confirmed (input method, profile shapes supported, etc.)
      body: 'Εκτός από επίπεδο έλασμα, το Workshop Assistant υποστηρίζει και κοστολόγηση κοπής laser για μεταλλικούς σωλήνες και προφίλ, ώστε να μπορείτε να δίνετε προσφορές για εξαρτήματα σωλήνα από την ίδια εφαρμογή που ήδη χρησιμοποιείτε για τα ελάσματά σας.',
    },
    features: {
      title: 'Τι περιλαμβάνει',
      items: [
        // TODO: confirm/replace each of these 4 with the real capability set
        { icon: 'zap', title: 'Κόστος Κοπής Σωλήνα', description: 'Υπολογισμός κόστους κοπής laser για εξαρτήματα σωλήνα, ενσωματωμένος στην ίδια ροή εργασίας κοστολόγησης.' },
        { icon: 'calc', title: 'Ίδια Λογική Κοστολόγησης', description: 'Χρησιμοποιεί τον ίδιο μηχανισμό υπολογισμού υλικού, εργατικών και ρεύματος με τα υπόλοιπα εξαρτήματα ελάσματος.' },
        { icon: 'settings', title: 'Παραμετροποιήσιμες Ρυθμίσεις', description: 'Προσαρμόστε τιμές μηχανήματος και υλικού για την κοπή σωλήνα στον Επεξεργαστή Παραμέτρων.' },
        { icon: 'excel', title: 'Ενιαία Αναφορά Excel', description: 'Εξαρτήματα σωλήνα εμφανίζονται στην ίδια αναφορά προσφοράς Excel με τα υπόλοιπα εξαρτήματα.' },
      ],
    },
    faq: [
      // TODO: verify these answers reflect the actual tube laser workflow
      { q: 'Υποστηρίζει το Workshop Assistant κοστολόγηση κοπής σωλήνα;', a: 'Ναι, εκτός από επίπεδο έλασμα και CNC router, το Workshop Assistant υπολογίζει κόστος και για εξαρτήματα κοπής laser σωλήνα.' },
      { q: 'Χρειάζεται ξεχωριστή εφαρμογή για σωλήνα;', a: 'Όχι, η κοστολόγηση σωλήνα γίνεται μέσα από την ίδια εφαρμογή που χρησιμοποιείτε για έλασμα.' },
    ],
    cta: { title: 'Ρωτήστε μας για την Κοστολόγηση Σωλήνα', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή ή επικοινωνήστε μαζί μας για να δείτε πώς λειτουργεί η κοστολόγηση κοπής σωλήνα στην πράξη.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Tube Laser Cutting Costing',
      subtitle: 'Calculate laser cutting cost for metal tube and profile automatically, using the same costing engine you already use for sheet metal.',
      cta1: 'Download Free Trial',
      cta2: 'Contact Us',
    },
    intro: {
      title: 'Tube costing, not just flat sheet',
      // TODO: replace with the real description of how tube laser costing
      // works once confirmed (input method, profile shapes supported, etc.)
      body: 'Beyond flat sheet metal, Workshop Assistant also supports laser cutting cost estimation for metal tube and profile, so you can quote tube parts from the same application you already use for your sheet metal work.',
    },
    features: {
      title: "What's included",
      items: [
        // TODO: confirm/replace each of these 4 with the real capability set
        { icon: 'zap', title: 'Tube Cutting Cost', description: 'Laser cutting cost calculation for tube parts, built into the same costing workflow.' },
        { icon: 'calc', title: 'Same Costing Logic', description: 'Uses the same material, labor, and electricity calculation engine as the rest of your sheet metal parts.' },
        { icon: 'settings', title: 'Configurable Settings', description: 'Adjust machine and material rates for tube cutting in the Parameters Editor.' },
        { icon: 'excel', title: 'Unified Excel Report', description: 'Tube parts appear in the same offer Excel report alongside your other parts.' },
      ],
    },
    faq: [
      // TODO: verify these answers reflect the actual tube laser workflow
      { q: 'Does Workshop Assistant support tube laser cutting costing?', a: 'Yes, in addition to flat sheet metal and CNC router, Workshop Assistant calculates cost for tube laser cutting parts too.' },
      { q: 'Do I need a separate application for tube work?', a: 'No, tube costing runs through the same application you use for sheet metal.' },
    ],
    cta: { title: 'Ask Us About Tube Laser Costing', subtitle: 'Download the free trial or get in touch to see tube laser cutting costing in action.', btn: 'Download Free Trial' },
  },
}

const icons = { zap: <Zap className="h-10 w-10" />, calc: <Calculator className="h-10 w-10" />, settings: <Settings className="h-10 w-10" />, excel: <FileSpreadsheet className="h-10 w-10" /> }

const TubeLaserCosting = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Tube Laser Cutting Cost Calculator"
        description="Calculate laser cutting costs for metal tube and profile parts with Workshop Assistant, using the same costing engine as your sheet metal work."
        path="/tube-laser-costing"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/contact" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
            <p className="text-lg text-gray-600">{t.intro.body}</p>
          </div>
          {/* TODO: swap for a real tube-costing screenshot once available — reusing the calculator screenshot for now */}
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/calculator.png?v=2`} alt={t.hero.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default TubeLaserCosting
